import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES } from "../lib/site";
import { Reveal } from "../components/Reveal";
import { TikTokEmbed } from "../components/TikTokEmbed";

// Gallery uses the photos sent by the owner
const PHOTOS = [...IMAGES.userPhotos];

const spans = [
  "md:col-span-2 md:row-span-2",
  "",
  "md:row-span-2",
  "",
  "md:col-span-2",
  "",
  "",
  "md:row-span-2",
  "md:col-span-2",
  "",
  "",
];

export default function GalleryPage() {
  const { t } = useLang();
  const [active, setActive] = useState(null);

  return (
    <main data-testid="gallery-page">
      <section className="relative pt-40 md:pt-52 pb-12 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-gold/60" />
            <span className="font-body text-xs tracking-[0.3em] uppercase text-gold">{t("gallery.kicker")}</span>
          </motion.div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight">{t("gallery.title")}</h1>
          <p className="mt-5 font-body text-cream/60 max-w-md">{t("gallery.sub")}</p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24">
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4">
          {PHOTOS.map((src, i) => (
            <Reveal
              key={i}
              variant="scale"
              delay={(i % 4) * 0.05}
              className={`relative overflow-hidden group cursor-pointer ${spans[i] || ""}`}
            >
              <button
                onClick={() => setActive(src)}
                data-testid={`gallery-item-${i}`}
                className="w-full h-full"
                aria-label={`Foto ${i + 1}`}
              >
                <img src={src} alt={`Mamma Rosa ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <span className="absolute inset-0 bg-navy-deep/0 group-hover:bg-navy-deep/20 transition-colors" />
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TikTok videos */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-32" data-testid="tiktok-section">
        <Reveal>
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-gold/60" />
            <span className="font-body text-xs tracking-[0.3em] uppercase text-gold">{t("gallery.videosKicker")}</span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight">{t("gallery.videosTitle")}</h2>
          <p className="mt-4 font-body text-cream/60 max-w-md">{t("gallery.videosSub")}</p>
        </Reveal>
        <TikTokEmbed />
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[200] bg-navy-deep/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
            data-testid="lightbox"
          >
            <button className="absolute top-6 right-6 text-cream/70 hover:text-gold" onClick={() => setActive(null)} aria-label="Chiudi" data-testid="lightbox-close">
              <X size={32} />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              src={active}
              alt="Mamma Rosa"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
